import type React from "react";
import { createStore } from "@/src/shared/utils/createStore";
import {
  heroFlightService,
  type IRect,
} from "@/src/shared/services/heroFlight";

/** One card ⇄ details photo flight (`HeroFlight` plays it). */
interface IHeroFlight {
  listingId: number;
  /** Shown on the loading sheet, as the app pre-fills the header from the card. */
  title: string;
  /** The photo on show on the card, already loaded. */
  src: string;
  /** The card photo's box when it was tapped. */
  cardRect: IRect;
  direction: "open" | "close";
  /** The card link's href, followed once the flight has started. */
  href: string;
  /** Pathnames of the page the card was on and of the details page it opened. */
  fromPath: string;
  detailsPath: string;
}

interface IHeroFlightState {
  flight: IHeroFlight | null;
  /** The last card opened, so going back to its page flies the photo home. */
  last: IHeroFlight | null;
}

export const heroFlightStore = createStore<IHeroFlightState>({
  flight: null,
  last: null,
});

/** Marks a card's photo frame (`data-hero-id={listing.id}`); the flight measures it. */
const HERO_FRAME_ATTR = "data-hero-id";
/** Marks a card whose links share one photo frame (the frame is not inside each link). */
const HERO_CARD_ATTR = "data-hero-card";

/** Every photo frame of `listingId`'s cards on the page (a listing can sit in two rails). */
export const findHeroFrames = (listingId: number): HTMLElement[] =>
  Array.from(
    document.querySelectorAll<HTMLElement>(
      `[${HERO_FRAME_ATTR}="${listingId}"]`,
    ),
  );

/**
 * A tap on a card's link: on a phone, starts the app's photo flight into the details
 * page and takes over the link's navigation, which `HeroFlight` starts right after.
 */
export function flyFromCard(
  event: React.MouseEvent<HTMLAnchorElement>,
  listingId: number,
  title: string,
): void {
  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  if (!heroFlightService.shouldFly(event, window.innerWidth, reducedMotion))
    return;

  // The link's own photo; a link without one (a card's title) flies the card's photo.
  const frame =
    event.currentTarget.querySelector<HTMLElement>(`[${HERO_FRAME_ATTR}]`) ??
    event.currentTarget
      .closest(`[${HERO_CARD_ATTR}]`)
      ?.querySelector<HTMLElement>(`[${HERO_FRAME_ATTR}]`);
  if (!frame) return;
  const box = frame.getBoundingClientRect();
  // The slide on show is the photo whose box starts where the frame does.
  const photos = Array.from(frame.querySelectorAll("img"));
  const shown =
    photos.find(
      (img) => Math.abs(img.getBoundingClientRect().left - box.left) < 1,
    ) ?? photos[0];
  const src = shown?.currentSrc || shown?.src;
  if (!src) return;

  heroFlightStore.set({
    flight: {
      listingId,
      title,
      src,
      cardRect: {
        left: box.left,
        top: box.top,
        width: box.width,
        height: box.height,
      },
      direction: "open",
      href:
        event.currentTarget.getAttribute("href") ?? event.currentTarget.href,
      fromPath: window.location.pathname,
      detailsPath: new URL(event.currentTarget.href).pathname,
    },
  });
  // `HeroFlight` follows the link once the photo is moving, so the next page's loading
  // doesn't land on the flight's first frames.
  event.preventDefault();
}
